import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiMoneyDollarCircleLine,
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiCalendarLine,
  RiUserLine,
  RiFileTextLine,
  RiCalculatorLine,
  RiBankCardLine,
  RiTimeLine,
  RiArrowDownSLine,
  RiInformationLine,
  RiStarLine,
  RiAlarmWarningLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/salary`;
const STAFF_URL = `${process.env.REACT_APP_API_URL}/admin`;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const statusConfig = {
  pending: {
    label: "Pending",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  processed: {
    label: "Processed",
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
  },
  paid: {
    label: "Paid",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
};

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n ?? 0);

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
        ${
          type === "success"
            ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
            : "bg-red-50 border border-red-200 text-red-800"
        }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children, maxWidth = "max-w-lg" }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${maxWidth} mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

const StatCard = ({ label, value, icon: Icon, borderColor }) => (
  <div
    className={`flex items-center gap-4 bg-white border ${borderColor} rounded-xl px-5 py-4 shadow-sm`}
  >
    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">
        {label}
      </p>
      <p className="text-lg font-bold text-slate-800 leading-tight">{value}</p>
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, options, icon: Icon }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
      {label}
    </label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-8 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <RiArrowDownSLine className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
    </div>
  </div>
);

export default function StaffSalary() {
  const [salaries, setSalaries] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [autoRecalculated, setAutoRecalculated] = useState(false);
  const [setForm, setSetForm] = useState({ staffId: "", baseSalary: "" });
  const [editForm, setEditForm] = useState({ baseSalary: "", notes: "" });
  const [payForm, setPayForm] = useState({ paymentDate: "", notes: "" });
  const [recalculatingId, setRecalculatingId] = useState(null);

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchSalaries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/month/${filterMonth}/${filterYear}`,
      );
      const data = res.data?.salaries || res.data || [];

      // DEBUG: Log the first salary record to see its structure
      if (data.length > 0) {
        console.log("First salary record:", data[0]);
        console.log("Metadata:", data[0].metadata);
        console.log("Advance Deduction:", data[0].metadata?.advanceDeduction);
      }

      setSalaries(data);
    } catch {
      notify("Failed to fetch salaries", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${STAFF_URL}/all-users`);
      const all = res.data?.data || res.data || [];
      setStaffList(all.filter((u) => u.userType === "staff"));
    } catch {}
  };

  useEffect(() => {
    fetchSalaries();
  }, [filterMonth, filterYear]);
  useEffect(() => {
    fetchStaff();
  }, []);

  const summary = {
    total: salaries.length,
    totalBase: salaries.reduce((s, x) => s + (x.baseSalary || 0), 0),
    totalCalc: salaries.reduce((s, x) => s + (x.calculatedSalary || 0), 0),
    totalAdvanceDeduction: salaries.reduce(
      (s, x) => s + (x.metadata?.advanceDeduction || 0),
      0,
    ),
    paid: salaries.filter((x) => x.status === "paid").length,
  };

  const handleSetSalary = async () => {
    if (!setForm.staffId || !setForm.baseSalary) {
      return notify("Staff and base salary are required", "error");
    }
    setActionLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/set`, {
        staffId: setForm.staffId,
        baseSalary: parseFloat(setForm.baseSalary),
        month: filterMonth,
        year: filterYear,
      });

      const summary = response.data?.data?.summary;
      if (summary) {
        notify(
          `Salary set for ${MONTHS[filterMonth - 1]} ${filterYear}. Net: ${fmt(summary.netSalary)}`,
          "success",
        );
      } else {
        notify("Salary set successfully");
      }

      fetchSalaries();
      setModal(null);
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to set salary", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecalculate = async (salary) => {
    setRecalculatingId(salary._id);
    try {
      const response = await axios.post(
        `${BASE_URL}/calculate/${salary.staffId?._id || salary.staffId}/${salary.month}/${salary.year}`,
      );

      const data = response.data?.data;
      if (data) {
        notify(
          `Salary recalculated: Net: ${fmt(data.calculatedSalary)}`,
          "success",
        );
      } else {
        notify("Salary recalculated successfully");
      }

      fetchSalaries();
    } catch {
      notify("Failed to recalculate", "error");
    } finally {
      setRecalculatingId(null);
    }
  };

  const handleRefreshAllSalaries = async () => {
    setRefreshingAll(true);
    let successCount = 0;
    let failCount = 0;

    for (const salary of salaries) {
      try {
        const staffId = salary.staffId?._id || salary.staffId;
        await axios.post(
          `${BASE_URL}/calculate/${staffId}/${salary.month}/${salary.year}`,
        );
        successCount++;
      } catch (error) {
        console.error(`Failed to recalculate salary:`, error);
        failCount++;
      }
    }

    notify(
      `Recalculated ${successCount} salaries successfully. ${failCount} failed.`,
      failCount > 0 ? "error" : "success",
    );

    fetchSalaries();
    setRefreshingAll(false);
  };

  const autoRecalculateOnLoad = async () => {
    if (salaries.length === 0 || autoRecalculated || loading) return;

    console.log("Auto-recalculating salaries on page load...");
    let successCount = 0;
    let failCount = 0;

    for (const salary of salaries) {
      try {
        const staffId = salary.staffId?._id || salary.staffId;
        await axios.post(
          `${BASE_URL}/calculate/${staffId}/${salary.month}/${salary.year}`,
        );
        successCount++;
      } catch (error) {
        console.error(`Failed to recalculate:`, error);
        failCount++;
      }
    }

    if (successCount > 0) {
      console.log(`Auto-recalculated ${successCount} salaries`);
      setAutoRecalculated(true);
      fetchSalaries();
    }
  };

  useEffect(() => {
    if (salaries.length > 0 && !autoRecalculated && !loading) {
      autoRecalculateOnLoad();
    }
  }, [salaries, loading, autoRecalculated]);

  const handleUpdate = async () => {
    if (!editForm.baseSalary) return notify("Base salary required", "error");
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/update/${selected._id}`, {
        baseSalary: parseFloat(editForm.baseSalary),
        notes: editForm.notes,
      });
      notify("Salary updated");
      fetchSalaries();
      setModal(null);
    } catch {
      notify("Failed to update salary", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleProcess = async (salary) => {
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/process/${salary._id}`);
      notify("Salary marked as processed");
      fetchSalaries();
    } catch {
      notify("Failed to process", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/pay/${selected._id}`, {
        paymentDate: payForm.paymentDate || undefined,
        notes: payForm.notes || undefined,
      });
      notify("Salary marked as paid");
      fetchSalaries();
      setModal(null);
    } catch {
      notify("Failed to mark as paid", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/${selected._id}`);
      notify("Salary record deleted");
      fetchSalaries();
      setModal(null);
    } catch {
      notify("Failed to delete", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const openSet = () => {
    setSetForm({ staffId: staffList[0]?._id || "", baseSalary: "" });
    setModal("set");
  };
  const openEdit = (s) => {
    setSelected(s);
    setEditForm({ baseSalary: s.baseSalary, notes: s.notes || "" });
    setModal("edit");
  };
  const openPay = (s) => {
    setSelected(s);
    setPayForm({ paymentDate: "", notes: "" });
    setModal("pay");
  };
  const openDelete = (s) => {
    setSelected(s);
    setModal("delete");
  };
  const openDetail = (s) => {
    setSelected(s);
    setModal("detail");
  };
  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };

  const filtered = salaries.filter((s) => {
    const name = s.staffId?.name || "";
    const email = s.staffId?.email || "";
    return (
      name.toLowerCase().includes(search.toLowerCase()) ||
      email.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiMoneyDollarCircleLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Staff Salary
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Salary Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="relative">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 pointer-events-none" />
                <select
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(Number(e.target.value))}
                  className="appearance-none bg-blue-50 border border-blue-200 rounded-lg pl-8 pr-7 py-2 text-sm text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                >
                  {MONTHS.map((m, i) => (
                    <option key={i + 1} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
                <RiArrowDownSLine className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 pointer-events-none" />
              </div>
              <div className="relative">
                <select
                  value={filterYear}
                  onChange={(e) => setFilterYear(Number(e.target.value))}
                  className="appearance-none bg-blue-50 border border-blue-200 rounded-lg px-3 pr-7 py-2 text-sm text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <RiArrowDownSLine className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 pointer-events-none" />
              </div>
            </div>

            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search staff…"
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-48 transition"
              />
            </div>

            <button
              onClick={fetchSalaries}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={handleRefreshAllSalaries}
              disabled={refreshingAll || loading}
              title="Recalculate all salaries for current month"
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-purple-200"
            >
              {refreshingAll ? (
                <Spinner />
              ) : (
                <RiCalculatorLine className="w-4 h-4" />
              )}
              Recalculate All
            </button>

            <button
              onClick={openSet}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              Set Salary
            </button>
          </div>
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Staff"
            value={summary.total}
            icon={RiUserLine}
            borderColor="border-blue-100"
          />
          <StatCard
            label="Base Salary Total"
            value={fmt(summary.totalBase)}
            icon={RiMoneyDollarCircleLine}
            borderColor="border-purple-100"
          />
          {/* <StatCard
            label="Advance Deductions"
            value={fmt(summary.totalAdvanceDeduction)}
            icon={RiAlarmWarningLine}
            borderColor="border-orange-100"
          /> */}
          <StatCard
            label="Net Payable"
            value={fmt(summary.totalCalc)}
            icon={RiCalculatorLine}
            borderColor="border-amber-100"
          />
          <StatCard
            label="Paid"
            value={`${summary.paid} / ${summary.total}`}
            icon={RiBankCardLine}
            borderColor="border-emerald-100"
          />
        </div>

        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiMoneyDollarCircleLine className="w-4 h-4 text-blue-500" />
              {MONTHS[filterMonth - 1]} {filterYear} — Salary Records
            </span>
            <span className="text-xs text-blue-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {[
                    "Staff",
                    "Base Salary",
                    "Attendance",
                    "Extra Details",
                    // "Deductions",
                    "Net Salary",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th key={h} className="px-4 py-3 text-left">
                      <span className="text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading salaries…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiMoneyDollarCircleLine className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search
                            ? "No records match your search."
                            : "No salary records for this period."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((s, i) => {
                    const cfg = statusConfig[s.status] || statusConfig.pending;
                    const staff = s.staffId || {};
                    const isRecalculating = recalculatingId === s._id;

                    const regularDays = s.regularPresentDays || 0;
                    const extraDays = s.extraDays || 0;
                    const totalPresentDays = regularDays + extraDays;
                    const absentDays = s.absentDays || 0;
                    const holidaysTaken = s.holidaysTaken || 0;
                    const advanceDeduction = s.metadata?.advanceDeduction || 0;
                    const holidayDeduction = s.metadata?.holidayDeduction || 0;
                    const hasDeductions =
                      holidayDeduction > 0 || advanceDeduction > 0;

                    return (
                      <tr
                        key={s._id || i}
                        className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            {staff.profilePic ? (
                              <img
                                src={staff.profilePic}
                                alt={staff.name}
                                className="w-8 h-8 rounded-full object-cover border border-blue-100"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                                {(staff.name || "?")[0].toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-slate-800 whitespace-nowrap">
                                {staff.name || "—"}
                              </p>
                              <p className="text-[11px] text-slate-400">
                                {staff.position || "Staff"}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">
                          {fmt(s.baseSalary)}
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <RiCheckLine className="w-3 h-3" />
                                {regularDays}d
                              </span>
                              {absentDays > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200">
                                  <RiCloseLine className="w-3 h-3" />
                                  {absentDays}d
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {extraDays > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                                <RiStarLine className="w-3 h-3" />+{extraDays}d
                              </span>
                            )}
                            {holidaysTaken > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-600 border border-orange-200">
                                <RiCalendarLine className="w-3 h-3" />
                                {holidaysTaken}d
                              </span>
                            )}
                          </div>
                        </td>

                        {/* <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            {holidayDeduction > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-600 border border-orange-200 w-fit">
                                Holiday: -{fmt(holidayDeduction)}
                              </span>
                            )}
                            {advanceDeduction > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200 w-fit">
                                <RiAlarmWarningLine className="w-3 h-3" />
                                Advance: -{fmt(advanceDeduction)}
                              </span>
                            )}
                            {!hasDeductions && (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </div>
                        </td> */}

                        <td className="px-4 py-3">
                          <div>
                            <span className="font-bold text-slate-800 whitespace-nowrap">
                              {fmt(s.calculatedSalary)}
                            </span>
                            {s.metadata?.grossSalary && (
                              <div className="text-[10px] text-slate-400">
                                Gross: {fmt(s.metadata.grossSalary)}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}
                          >
                            {cfg.label}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex gap-1.5 flex-wrap">
                            <button
                              onClick={() => openDetail(s)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 font-semibold transition"
                              title="View Details"
                            >
                              <RiFileTextLine className="w-3.5 h-3.5" /> View
                            </button>
                            <button
                              onClick={() => handleRecalculate(s)}
                              disabled={isRecalculating}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-purple-50 border border-purple-200 text-purple-600 hover:bg-purple-100 font-semibold transition disabled:opacity-50"
                              title="Recalculate Salary"
                            >
                              {isRecalculating ? (
                                <Spinner />
                              ) : (
                                <RiCalculatorLine className="w-3.5 h-3.5" />
                              )}{" "}
                              Calc
                            </button>
                            <button
                              onClick={() => openEdit(s)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                              title="Edit Salary"
                            >
                              <RiEditLine className="w-3.5 h-3.5" /> Edit
                            </button>
                            {s.status === "pending" && (
                              <button
                                onClick={() => handleProcess(s)}
                                disabled={actionLoading}
                                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-600 hover:bg-amber-100 font-semibold transition disabled:opacity-50"
                                title="Process Salary"
                              >
                                <RiTimeLine className="w-3.5 h-3.5" /> Process
                              </button>
                            )}
                            {s.status === "processed" && (
                              <button
                                onClick={() => openPay(s)}
                                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100 font-semibold transition"
                                title="Mark as Paid"
                              >
                                <RiBankCardLine className="w-3.5 h-3.5" /> Pay
                              </button>
                            )}
                            <button
                              onClick={() => openDelete(s)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                              title="Delete Record"
                            >
                              <RiDeleteBinLine className="w-3.5 h-3.5" /> Del
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Set Salary Modal */}
      {modal === "set" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Set Staff Salary
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-4 mb-6">
            <SelectField
              label="Select Staff"
              icon={RiUserLine}
              value={setForm.staffId}
              onChange={(v) => setSetForm((f) => ({ ...f, staffId: v }))}
              options={staffList.map((s) => ({
                value: s._id,
                label: `${s.name} — ${s.position || "Staff"}`,
              }))}
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Base Salary (₹)
              </label>
              <div className="relative">
                <RiMoneyDollarCircleLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="number"
                  value={setForm.baseSalary}
                  onChange={(e) =>
                    setSetForm((f) => ({ ...f, baseSalary: e.target.value }))
                  }
                  placeholder="e.g. 31000"
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
              </div>
            </div>

            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-blue-700 flex items-start gap-1.5">
                <RiInformationLine className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Salary Calculation:</strong>
                  <br />• Gross = (Regular + Extra Days) × Daily Rate
                  <br />• Holiday Deduction = (Holidays - 1) × Daily Rate
                  <br />• Advance Deduction = Monthly installment if any
                  <br />• Net = Gross - Holiday Deduction - Advance Deduction
                </span>
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleSetSalary}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}{" "}
              Set Salary
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === "edit" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit —{" "}
              {selected?.staffId?.name}
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Base Salary (₹)
              </label>
              <div className="relative">
                <RiMoneyDollarCircleLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="number"
                  value={editForm.baseSalary}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, baseSalary: e.target.value }))
                  }
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Notes
              </label>
              <div className="relative">
                <RiFileTextLine className="absolute left-3 top-3 w-4 h-4 text-blue-300" />
                <textarea
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  rows={3}
                  placeholder="Optional notes…"
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}{" "}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* Pay Modal */}
      {modal === "pay" && (
        <Modal
          title={
            <>
              <RiBankCardLine className="w-5 h-5 text-emerald-500" /> Mark as
              Paid — {selected?.staffId?.name}
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-4 mb-6">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
              <RiMoneyDollarCircleLine className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">
                  Net Payable
                </p>
                <p className="text-lg font-bold text-emerald-700">
                  {fmt(selected?.calculatedSalary)}
                </p>
              </div>
            </div>

            {selected?.metadata?.advanceDeduction > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                  <RiAlarmWarningLine className="w-3.5 h-3.5" />
                  Advance Deduction Details
                </p>
                <p className="text-sm text-red-600">
                  {fmt(selected.metadata.advanceDeduction)} will be deducted
                  from this month's salary
                </p>
                {selected.metadata?.advanceDetails?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {selected.metadata.advanceDetails.map((adv, idx) => (
                      <div key={idx} className="text-xs text-red-500">
                        • {adv.reason || "Advance"}: {fmt(adv.monthlyPayAmount)}
                        /month (Remaining: {fmt(adv.remaining)})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Payment Date
              </label>
              <div className="relative">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="date"
                  value={payForm.paymentDate}
                  onChange={(e) =>
                    setPayForm((f) => ({ ...f, paymentDate: e.target.value }))
                  }
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Notes
              </label>
              <div className="relative">
                <RiFileTextLine className="absolute left-3 top-3 w-4 h-4 text-blue-300" />
                <textarea
                  value={payForm.notes}
                  onChange={(e) =>
                    setPayForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  rows={2}
                  placeholder="Optional notes…"
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleMarkPaid}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition shadow-md shadow-emerald-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiBankCardLine className="w-4 h-4" />
              )}{" "}
              Confirm Payment
            </button>
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {modal === "detail" && selected && (
        <Modal
          title={
            <>
              <RiFileTextLine className="w-5 h-5 text-blue-500" /> Salary Detail
              — {selected.staffId?.name}
            </>
          }
          onClose={closeModal}
          maxWidth="max-w-lg"
        >
          {(() => {
            const s = selected;
            const cfg = statusConfig[s.status] || statusConfig.pending;
            const staff = s.staffId || {};

            const regularDays = s.regularPresentDays ?? 0;
            const extraDays = s.extraDays ?? 0;
            const totalPresent = regularDays + extraDays;
            const absentDays = s.absentDays ?? 0;
            const holidaysTaken = s.holidaysTaken ?? 0;
            const advanceDeduction = s.metadata?.advanceDeduction || 0;
            const advanceDetails = s.metadata?.advanceDetails || [];

            return (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                  {staff.profilePic ? (
                    <img
                      src={staff.profilePic}
                      alt={staff.name}
                      className="w-10 h-10 rounded-full object-cover border border-blue-200"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                      {(staff.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-slate-800">
                      {staff.name || "—"}
                    </p>
                    <p className="text-xs text-slate-400">
                      {staff.email || ""}
                    </p>
                  </div>
                  <span
                    className={`ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}
                  >
                    {cfg.label}
                  </span>
                </div>

                {/* Salary Breakdown Section */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <h3 className="text-sm font-bold text-blue-900">
                    Salary Breakdown
                  </h3>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-blue-700 font-semibold">
                        Daily Rate
                      </span>
                      <span className="text-sm font-bold text-blue-900">
                        {fmt(s.metadata?.dailyRate || s.baseSalary / 30)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-blue-700 font-semibold">
                        Regular Present Days
                      </span>
                      <span className="text-sm font-bold text-emerald-700">
                        {regularDays} days
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-blue-700 font-semibold">
                        Extra Days Worked
                      </span>
                      <span className="text-sm font-bold text-purple-700">
                        {extraDays} days
                      </span>
                    </div>
                    {absentDays > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-red-600 font-semibold">
                          Absent Days
                        </span>
                        <span className="text-sm font-bold text-red-600">
                          {absentDays} days
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-blue-200 pt-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-emerald-700 font-semibold">
                        Gross Salary
                      </span>
                      <span className="text-sm font-bold text-emerald-700">
                        {fmt(s.metadata?.grossSalary || 0)}
                      </span>
                    </div>
                    {s.metadata?.holidayDeduction > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-orange-700 font-semibold">
                          Holiday Deduction (
                          {holidaysTaken > 1 ? holidaysTaken - 1 : 0} days)
                        </span>
                        <span className="text-sm font-bold text-orange-700">
                          -{fmt(s.metadata.holidayDeduction)}
                        </span>
                      </div>
                    )}
                    {advanceDeduction > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-red-700 font-semibold flex items-center gap-1">
                          <RiAlarmWarningLine className="w-3 h-3" />
                          Advance Deduction
                        </span>
                        <span className="text-sm font-bold text-red-700">
                          -{fmt(advanceDeduction)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-blue-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-blue-900">
                        Net Salary
                      </span>
                      <span className="text-lg font-bold text-emerald-600">
                        {fmt(s.calculatedSalary)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Advance Details if any */}
                {advanceDetails.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <h3 className="text-sm font-bold text-red-900 mb-3 flex items-center gap-2">
                      <RiAlarmWarningLine className="w-4 h-4" />
                      Active Advance Payments
                    </h3>
                    <div className="space-y-2">
                      {advanceDetails.map((adv, idx) => (
                        <div
                          key={idx}
                          className="p-3 bg-white rounded-lg border border-red-100"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm font-semibold text-slate-700">
                              {adv.reason || "Salary Advance"}
                            </span>
                            <span className="text-sm font-bold text-red-600">
                              {fmt(adv.monthlyPayAmount)}/month
                            </span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500">
                              Total Advance:
                            </span>
                            <span className="font-semibold text-slate-700">
                              {fmt(adv.advanceAmount)}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs mt-1">
                            <span className="text-slate-500">Remaining:</span>
                            <span className="font-semibold text-orange-600">
                              {fmt(adv.remaining)}
                            </span>
                          </div>
                          <div className="mt-2 w-full bg-slate-200 rounded-full h-1.5">
                            <div
                              className="bg-red-500 h-1.5 rounded-full"
                              style={{
                                width: `${((adv.advanceAmount - adv.remaining) / adv.advanceAmount) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      label: "Month / Year",
                      value: `${MONTHS[s.month - 1]} ${s.year}`,
                    },
                    { label: "Base Salary", value: fmt(s.baseSalary) },
                    {
                      label: "Total Days in Month",
                      value: s.metadata?.totalDaysInMonth || "—",
                    },
                    {
                      label: "Total Working Days",
                      value: s.metadata?.totalWorkingDays || "—",
                    },
                    {
                      label: "Total Present Days",
                      value: `${totalPresent} days`,
                    },
                    {
                      label: "Holidays Taken",
                      value: `${holidaysTaken} days`,
                    },
                    {
                      label: "Payment Date",
                      value: s.paymentDate
                        ? new Date(s.paymentDate).toLocaleDateString("en-IN")
                        : "—",
                    },
                  ].map(({ label, value }) => (
                    <div
                      key={label}
                      className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5"
                    >
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                        {label}
                      </p>
                      <p className="text-sm font-bold text-slate-800">
                        {value}
                      </p>
                    </div>
                  ))}
                </div>

                {s.notes && (
                  <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-sm text-slate-600">
                    <span className="font-semibold text-amber-700">
                      Notes:{" "}
                    </span>
                    {s.notes}
                  </div>
                )}

                <div className="flex justify-end pt-2 border-t border-blue-50">
                  <button
                    onClick={closeModal}
                    className="px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}

      {/* Delete Modal */}
      {modal === "delete" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete salary record for{" "}
              <span className="text-red-500">{selected?.staffId?.name}</span>?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This action cannot be undone. The salary record will be
              permanently removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}{" "}
                Delete Record
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
